import { useLayoutEffect, useMemo } from 'react'
import {
  buildMultiSelectShapeSnap,
  chromeNearlyEqual,
  computeMultiSelectChrome,
  getMultiSelectableIds,
  rigidTranslationForMultiSelect,
} from '../helpers/multi-select-geometry'
import { MULTI_SELECT_RIGID_TRANSLATION_MIN_PX } from '../constants/shape-manager-constants'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useMultiSelectChromeSync(ctx: ShapeManagerStateApi) {
  const {
    interactionRef,
    multiSelectChromeRef,
    multiSelectSelectionKeyRef,
    multiSelectSyncPrevShapesRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    selectedShapeIds,
    setMultiSelectChrome,
    shapes,
    suppressMultiSelectChrome,
  } = ctx

  const multiSelectSelectionKey = useMemo(
    () => (selectedShapeIds.length > 1 ? [...selectedShapeIds].sort().join('|') : ''),
    [selectedShapeIds]
  )

  // Track selection set identity for panel rotate baseline; clear chrome when leaving multi-select.
  useLayoutEffect(() => {
    if (selectedShapeIds.length < 2) {
      multiSelectSelectionKeyRef.current = ''
      panelMultiRotateBaselineRef.current = null
      pendingRotateChromeBeforeRef.current = null
      multiSelectSyncPrevShapesRef.current = null
      multiSelectChromeRef.current = null
      setMultiSelectChrome((prev) => (prev !== null ? null : prev))
      return
    }

    if (multiSelectSelectionKey !== multiSelectSelectionKeyRef.current) {
      multiSelectSelectionKeyRef.current = multiSelectSelectionKey
      // New multi-select membership: drop panel baseline and stale chrome first. Otherwise an
      // orphaned baseline (e.g. panel slider never finished) makes the sync effect below skip,
      // leaving previous selection's pivot/size while `multiSelectUnionBounds` updates — the UI prefers
      // `multiSelectChrome` and draws a huge box (marquee after a prior multi-select rotate).
      panelMultiRotateBaselineRef.current = null
      pendingRotateChromeBeforeRef.current = null
      multiSelectSyncPrevShapesRef.current = null
      // Clear ref synchronously so the chrome sync effect below does not treat oriented chrome from
      // the prior membership as rigid-translation-only (e.g. add shape after rotate, then rotate again).
      multiSelectChromeRef.current = null
      setMultiSelectChrome(null)
    }
  }, [
    multiSelectSelectionKey,
    selectedShapeIds,
    multiSelectChromeRef,
    multiSelectSelectionKeyRef,
    multiSelectSyncPrevShapesRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    setMultiSelectChrome,
  ])

  // Keep oriented multi-select chrome in sync when shapes move/resize from keyboard, object panel, or
  // bounds updates. Skip while a pointer interaction is active — move/rotate handlers drive chrome.
  // Also skip during object-panel rotation slider: those updates change `shapes` every tick but
  // `computeMultiSelectChrome` is axis-aligned and always returns rotationDeg 0, which would
  // wipe the live OBB rotation applied in `applyPanelMultiSelectRotationPreview`.
  //
  // When the box is already oriented (non-zero rotationDeg), never replace chrome with that
  // axis-aligned union — it would reset the angle to 0° after canvas or panel rotation commits.
  // Keep pivot/size/rotation and only nudge the pivot on rigid translation (same dx,dy on every shape).
  //
  // No separate `shapeBounds`-driven refresh effect exists any more: `computeMultiSelectChrome`
  // is pure over `shapes` (`rotatedVisualAabbOfShape`, no DOM-measured bounds involved), so this
  // effect's own `[shapes, ...]` dependency already recomputes it the instant geometry changes —
  // there is no longer a bounds measurement that can arrive later and need its own trigger.
  useLayoutEffect(() => {
    if (selectedShapeIds.length < 2) return
    if (suppressMultiSelectChrome) return

    if (interactionRef.current !== null) return
    if (panelMultiRotateBaselineRef.current !== null) return

    const prevChrome = multiSelectChromeRef.current
    const selectableIds = getMultiSelectableIds(selectedShapeIds, shapes)
    const prevSnap = multiSelectSyncPrevShapesRef.current

    let merged: MultiSelectChrome

    if (!prevChrome) {
      const next = computeMultiSelectChrome(selectedShapeIds, shapes)

      if (!next) return

      merged = next
    } else if (Math.abs(prevChrome.rotationDeg) >= 0.5 && prevSnap) {
      const t = rigidTranslationForMultiSelect(selectableIds, prevSnap, shapes)
      const significant =
        t &&
        (Math.abs(t.dx) > MULTI_SELECT_RIGID_TRANSLATION_MIN_PX ||
          Math.abs(t.dy) > MULTI_SELECT_RIGID_TRANSLATION_MIN_PX)

      merged = significant
        ? {
            ...prevChrome,
            pivotX: prevChrome.pivotX + t!.dx,
            pivotY: prevChrome.pivotY + t!.dy,
          }
        : prevChrome
    } else {
      const next = computeMultiSelectChrome(selectedShapeIds, shapes)

      if (!next) return

      merged = chromeNearlyEqual(prevChrome, next) ? prevChrome : next
    }

    if (chromeNearlyEqual(multiSelectChromeRef.current, merged)) {
      multiSelectSyncPrevShapesRef.current = buildMultiSelectShapeSnap(selectableIds, shapes)

      return
    }

    setMultiSelectChrome(merged)
    multiSelectSyncPrevShapesRef.current = buildMultiSelectShapeSnap(selectableIds, shapes)
  }, [
    shapes,
    selectedShapeIds,
    suppressMultiSelectChrome,
    interactionRef,
    multiSelectChromeRef,
    multiSelectSyncPrevShapesRef,
    panelMultiRotateBaselineRef,
    setMultiSelectChrome,
  ])

  // CenterCanvas draws unified chrome; ShapeManager must not keep multi-select OBB state — sync
  // effects above skip while suppressed, so clear stale chrome/ref here.
  useLayoutEffect(() => {
    if (!suppressMultiSelectChrome) return

    panelMultiRotateBaselineRef.current = null
    pendingRotateChromeBeforeRef.current = null
    multiSelectChromeRef.current = null
    multiSelectSyncPrevShapesRef.current = null
    setMultiSelectChrome((prev) => (prev !== null ? null : prev))
  }, [
    suppressMultiSelectChrome,
    multiSelectChromeRef,
    multiSelectSyncPrevShapesRef,
    panelMultiRotateBaselineRef,
    pendingRotateChromeBeforeRef,
    setMultiSelectChrome,
  ])

  return { multiSelectSelectionKey }
}

export type MultiSelectChromeSyncApi = ReturnType<typeof useMultiSelectChromeSync>
